import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, BookOpen, HelpCircle, CheckCircle, Loader2 } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { useSubmitSuggestion } from '../hooks/useGuides';
import type { Guide } from '../types';

interface GuideSuggestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  guides: Guide[];
}

type SuggestionType = 'term' | 'faq';
type Step = 'type' | 'form' | 'confirm' | 'success';

const STEPS: Step[] = ['type', 'form', 'confirm', 'success'];

export function GuideSuggestionModal({ isOpen, onClose, guides }: GuideSuggestionModalProps) {
  const { t } = useTranslation('guides');
  const [step, setStep] = useState<Step>('type');
  const [suggestionType, setSuggestionType] = useState<SuggestionType | null>(null);
  const [selectedGuideId, setSelectedGuideId] = useState<string>('');
  const [category, setCategory] = useState('');

  // Term fields
  const [term, setTerm] = useState('');
  const [explanation, setExplanation] = useState('');

  // FAQ fields
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');

  const submitSuggestion = useSubmitSuggestion();

  const currentStepIndex = STEPS.indexOf(step);

  const handleTypeSelect = (type: SuggestionType) => {
    setSuggestionType(type);
    setStep('form');
  };

  const handleGoToConfirm = () => {
    setStep('confirm');
  };

  const handleSubmit = async () => {
    if (!suggestionType) return;

    const content = suggestionType === 'term'
      ? { term, explanation }
      : { question, answer };

    try {
      await submitSuggestion.mutateAsync({
        submissionType: suggestionType,
        suggestedGuideId: selectedGuideId || undefined,
        suggestedCategory: category || undefined,
        content,
      });

      setStep('success');
    } catch (error) {
      console.error('Error submitting suggestion:', error);
    }
  };

  const handleClose = () => {
    setStep('type');
    setSuggestionType(null);
    setSelectedGuideId('');
    setCategory('');
    setTerm('');
    setExplanation('');
    setQuestion('');
    setAnswer('');
    onClose();
  };

  const isFormValid = suggestionType === 'term'
    ? term.trim() && explanation.trim()
    : question.trim() && answer.trim();

  const selectedGuide = guides.find(g => g.id === selectedGuideId);

  if (!isOpen) return null;

  // Success view
  if (step === 'success') {
    const successContent = (
      <>
        <div className="bottom-sheet-backdrop" onClick={handleClose} />
        <div className="bottom-sheet-container">
          <div className="bottom-sheet-modal">
            <div className="bottom-sheet-header">
              <h2>{t('suggestion.successTitle')}</h2>
              <button
                onClick={handleClose}
                className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bottom-sheet-content text-center">
              <div className="w-16 h-16 bg-pp-teal/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-pp-teal" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">
                {t('suggestion.successMessage')}
              </h3>
              <p className="text-white/70">
                {t('suggestion.successDescription')}
              </p>
            </div>

            <div className="bottom-sheet-footer">
              <button
                onClick={handleClose}
                className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90"
              >
                {t('suggestion.close')}
              </button>
            </div>
          </div>
        </div>
      </>
    );
    return createPortal(successContent, document.body);
  }

  const modalContent = (
    <>
      <div className="bottom-sheet-backdrop" onClick={handleClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal">
          {/* Header */}
          <div className="bottom-sheet-header">
            <div className="flex-1">
              <h2 className="!text-lg !font-semibold">
                {step === 'type' && t('suggestion.title')}
                {step === 'form' && (suggestionType === 'term' ? t('suggestion.stepDetails.term') : t('suggestion.stepDetails.faq'))}
                {step === 'confirm' && t('suggestion.stepReview')}
              </h2>
              {/* Progress Indicator */}
              <div className="flex gap-1 mt-3">
                {STEPS.slice(0, -1).map((_, index) => (
                  <div
                    key={index}
                    className={`flex-1 h-1 rounded-full ${
                      index < currentStepIndex ? 'bg-pp-orange' : 'bg-white/20'
                    }`}
                  />
                ))}
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 ml-4"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="bottom-sheet-content">
            {/* Step 1: Type Selection */}
            {step === 'type' && (
              <div className="space-y-3">
                <p className="text-sm text-white/70 mb-4">
                  {t('suggestion.helpText')}
                </p>

                <button
                  onClick={() => handleTypeSelect('term')}
                  className="w-full glass-effect hover:bg-white/10 rounded-[15px] p-4 text-left transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-pp-dark/25 rounded-xl flex items-center justify-center">
                      <BookOpen className="w-6 h-6 text-pp-orange" />
                    </div>
                    <div>
                      <h3 className="font-medium text-white">{t('suggestion.typeTerm')}</h3>
                      <p className="text-sm text-white/70">
                        {t('suggestion.typeTermHint')}
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => handleTypeSelect('faq')}
                  className="w-full glass-effect hover:bg-white/10 rounded-[15px] p-4 text-left transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-pp-dark/25 rounded-xl flex items-center justify-center">
                      <HelpCircle className="w-6 h-6 text-pp-orange" />
                    </div>
                    <div>
                      <h3 className="font-medium text-white">{t('suggestion.typeFAQ')}</h3>
                      <p className="text-sm text-white/70">
                        {t('suggestion.typeFAQHint')}
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            )}

            {/* Step 2: Form */}
            {step === 'form' && (
              <div className="space-y-4">
                {/* Guide Selection */}
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">
                    {t('suggestion.guideLabel')}
                  </label>
                  <select
                    value={selectedGuideId}
                    onChange={(e) => setSelectedGuideId(e.target.value)}
                    className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-pp-orange"
                  >
                    <option value="" className="bg-pp-dark text-white">{t('suggestion.guideDefault')}</option>
                    {guides.map((guide) => (
                      <option key={guide.id} value={guide.id} className="bg-pp-dark text-white">
                        {guide.title}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">
                    {t('suggestion.categoryLabel')}
                  </label>
                  <input
                    type="text"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    placeholder={t('suggestion.categoryPlaceholder')}
                    className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange"
                  />
                </div>

                {/* Term Fields */}
                {suggestionType === 'term' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-1">
                        {t('suggestion.termLabel')}
                      </label>
                      <input
                        type="text"
                        value={term}
                        onChange={(e) => setTerm(e.target.value)}
                        placeholder={t('suggestion.termPlaceholder')}
                        className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-1">
                        {t('suggestion.explanationLabel')}
                      </label>
                      <textarea
                        value={explanation}
                        onChange={(e) => setExplanation(e.target.value)}
                        placeholder={t('suggestion.explanationPlaceholder')}
                        rows={4}
                        className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 resize-none focus:outline-none focus:ring-2 focus:ring-pp-orange"
                      />
                    </div>
                  </>
                )}

                {/* FAQ Fields */}
                {suggestionType === 'faq' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-1">
                        {t('suggestion.questionLabel')}
                      </label>
                      <input
                        type="text"
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        placeholder={t('suggestion.questionPlaceholder')}
                        className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-1">
                        {t('suggestion.answerLabel')}
                      </label>
                      <textarea
                        value={answer}
                        onChange={(e) => setAnswer(e.target.value)}
                        placeholder={t('suggestion.answerPlaceholder')}
                        rows={4}
                        className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 resize-none focus:outline-none focus:ring-2 focus:ring-pp-orange"
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Step 3: Confirm */}
            {step === 'confirm' && (
              <div className="space-y-4">
                <p className="text-sm text-white/70 mb-4">
                  {t('suggestion.reviewHint')}
                </p>

                <div className="glass-effect rounded-[15px] p-4 space-y-3">
                  <div>
                    <span className="text-xs text-white/50 uppercase tracking-wider">{t('suggestion.reviewType')}</span>
                    <p className="text-white font-medium">
                      {suggestionType === 'term' ? t('suggestion.reviewTerm') : t('suggestion.reviewFAQ')}
                    </p>
                  </div>

                  {selectedGuide && (
                    <div>
                      <span className="text-xs text-white/50 uppercase tracking-wider">{t('suggestion.reviewGuide')}</span>
                      <p className="text-white font-medium">{selectedGuide.title}</p>
                    </div>
                  )}

                  {category && (
                    <div>
                      <span className="text-xs text-white/50 uppercase tracking-wider">{t('suggestion.reviewCategory')}</span>
                      <p className="text-white font-medium">{category}</p>
                    </div>
                  )}

                  {suggestionType === 'term' && (
                    <>
                      <div>
                        <span className="text-xs text-white/50 uppercase tracking-wider">{t('suggestion.reviewTermLabel')}</span>
                        <p className="text-white font-medium">{term}</p>
                      </div>
                      <div>
                        <span className="text-xs text-white/50 uppercase tracking-wider">{t('suggestion.reviewExplanation')}</span>
                        <p className="text-white/90 text-sm">{explanation}</p>
                      </div>
                    </>
                  )}

                  {suggestionType === 'faq' && (
                    <>
                      <div>
                        <span className="text-xs text-white/50 uppercase tracking-wider">{t('suggestion.reviewQuestion')}</span>
                        <p className="text-white font-medium">{question}</p>
                      </div>
                      <div>
                        <span className="text-xs text-white/50 uppercase tracking-wider">{t('suggestion.reviewAnswer')}</span>
                        <p className="text-white/90 text-sm">{answer}</p>
                      </div>
                    </>
                  )}
                </div>

                <p className="text-xs text-white/50 text-center">
                  {t('suggestion.teamReview')}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bottom-sheet-footer">
            {step === 'type' && (
              <button
                onClick={handleClose}
                className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
              >
                {t('suggestion.cancel')}
              </button>
            )}

            {step === 'form' && (
              <>
                <button
                  onClick={() => setStep('type')}
                  className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
                >
                  {t('suggestion.back')}
                </button>
                <button
                  onClick={handleGoToConfirm}
                  disabled={!isFormValid}
                  className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('suggestion.next')}
                </button>
              </>
            )}

            {step === 'confirm' && (
              <>
                <button
                  onClick={() => setStep('form')}
                  className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
                >
                  {t('suggestion.back')}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitSuggestion.isPending}
                  className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium flex items-center justify-center hover:bg-pp-orange/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitSuggestion.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    t('suggestion.submit')
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
  return createPortal(modalContent, document.body);
}
